import { runBotBatch } from '../../../../../../shared/services/bot-bulk-actions';
import { NgTemplateOutlet } from '@angular/common';
import { Component, input, output, OnChanges } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HintComponent } from '@shared-angular/components/hint/hint.component';
import { AdminService } from '@shared-angular/services/admin.service';
import { LanguageService } from '@shared-angular/services/language.service';
import { LoginService } from '@shared-angular/services/login.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { ChatbotConfig } from '@shared/index';
import dayjs from 'dayjs';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TableModule } from 'primeng/table';
import { AdminPositionBubbleComponent } from "../../../../../../shared/components/ai-bot/utils/admin-position-bubble/admin-position-bubble.component";
import { TransferBotModalComponent } from '../../../../../../shared/components/ai-bot/utils/transfer-bot-modal/transfer-bot-modal.component';
import { BotIconComponent } from "../../../../../../shared/components/bot-icon/bot-icon.component";
import { BotService } from '../../../../../../shared/services/bot.service';
import { ProjectService } from '../../../../../../shared/services/project.service';
import { MoveBotToProjectComponent } from './move-bot-to-project/move-bot-to-project.component';

import { FormsModule } from '@angular/forms';
import { BotImportApiResponse, BotImportFailureReport } from '@shared-angular/services/bot-import.types';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Button, ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { Popover } from "primeng/popover";
import { TagModule } from "primeng/tag";
import { ProjectSubmenuComponent } from '../../../../../../project-submenu/project-submenu.component';
import { NewBotModalComponent } from '../home-page/new-bot-modal/new-bot-modal.component';
import { BulkAction, BulkActionBarComponent } from '../../../../../../shared/components/bulk-action-bar/bulk-action-bar.component';


@Component({
  selector: 'app-project-bots-tab',
  imports: [RouterLink, NgTemplateOutlet, TooltipModule,
    ProjectSubmenuComponent,
    HintComponent,
    BotIconComponent,
    AdminPositionBubbleComponent,
    ButtonModule,
    TableModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    Popover,
    ConfirmDialogModule,
    CheckboxModule,
    FormsModule,
    DialogModule,
    TagModule,
    BulkActionBarComponent,
    NewBotModalComponent
  ],
  standalone: true,
  providers: [ConfirmationService],
  templateUrl: './project-bots-tab.component.html',
  styleUrl: './project-bots-tab.component.scss'
})
export class ProjectBotsTabComponent implements OnChanges {
  public get bulkActions(): BulkAction[] {
    return [
      {
        id: 'to-archive',
        label: $localize`:@@to-archive:Archivieren`,
        icon: 'ri-archive-line',
        disabled: this.bulkBusy || !this.userCanEditBotConfig(),
        command: () => this.bulkArchive()
      },
      {
        id: 'bulk-to-trashbin',
        label: $localize`:@@bulk-to-trashbin:In den Papierkorb`,
        icon: 'ri-delete-bin-line',
        disabled: this.bulkBusy || !this.userCanEditBotConfig(),
        command: () => this.confirmBulkTrash()
      }
    ];
  }

  public readonly bots = input.required<ChatbotConfig[]>();
  public readonly searchString = input<string>("");
  public readonly VIEWMODE = input<"PROJECT" | "TRASH" | "ARCHIVE">("PROJECT");
  public readonly duplicateTrigger = output();
  public readonly reloadTrigger = output();
  public readonly deleteBot = output<string>();
  public readonly deleteTrashTrigger = output();
  public readonly tagClickTrigger = output<string>();

  protected templates: ChatbotConfig[] = [];
  protected showNewBotDialog = false;
  public dayjs = dayjs;
  public contextMenu: (MenuItem & { command: () => void })[] = [];
  private activePop: Popover | null = null;
  public selectedBots: ChatbotConfig[] = [];
  public bulkBusy = false;
  public bulkFailedNames = '';

  public ngOnChanges(): void {
    const ids = new Set(this.bots().filter(bot => !bot.archived && !bot.thisIsTrash).map(bot => bot._id));
    this.selectedBots = this.selectedBots.filter(bot => ids.has(bot._id));
  }

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private langService: LanguageService,
    public adminService: AdminService,
    public loginService: LoginService,
    public botService: BotService,
    private modalService: BsModalService,
    private projectService: ProjectService,
    private activatedRoute: ActivatedRoute,
    private confirmationService: ConfirmationService
  ) {
    this.activatedRoute.data.subscribe((data) => {
      this.templates = (data['templates'] as ChatbotConfig[] | undefined) ?? [];
    });
  }

  public openMenu(event: MouseEvent, popover: Popover, bot: ChatbotConfig, button: Button) {
    if (this.activePop && this.activePop !== popover) {
      this.activePop.hide();
    }
    this.contextMenu = this.getMenuItems(bot);
    popover.toggle(event, button.el.nativeElement);
    this.activePop = popover;
  }

  private getMenuItems(bot: ChatbotConfig) {
    const menu: (MenuItem & { command: () => void })[] = [];

    menu.push({
      label: $localize`:@@open:Öffnen`,
      icon: 'ri ri-external-link-line',
      command: () => {
        this.openBot(bot);
      }
    });

    menu.push({
      label: $localize`:@@duplicate:Duplizieren`,
      icon: 'ri ri-file-copy-2-line',
      command: async () => {
        await this.duplicateBot(bot._id);
      }
    });

    menu.push({
      label: $localize`:@@transfer:Übertragen`,
      icon: 'ri ri-user-shared-line',
      disabled: !this.userCanEditBotConfig(),
      command: async () => {
        await this.botTransfer(bot);
      }
    });

    menu.push({
      label: $localize`:@@move-project:Verschieben`,
      icon: 'ri ri-folder-transfer-line',
      disabled: !this.userCanEditBotConfig(),
      command: () => {
        this.moveBotToProject(bot);
      }
    });
    menu.push({
      label: $localize`:@@download:Herunterladen`,
      icon: 'ri ri-download-2-line',
      disabled: !this.userCanEditBotConfig(),
      command: async () => {
        await this.botService.exportBotConfigZIP(bot);
      }
    });

    menu.push({
      label: $localize`:@@to-archive:Archivieren`,
      icon: 'ri ri-archive-line',
      disabled: !this.userCanEditBotConfig(),
      command: async () => {
        await this.archiveBot(bot._id);
      }
    });

    menu.push({
      label: $localize`:@@to-the-trashbin:In den Papierkorb`,
      icon: 'ri ri-delete-bin-line',
      disabled: !this.userCanEditBotConfig(),
      command: () => {
        this.confirmationCheck(bot._id);
      }
    });

    return menu;
  }

  public openBot(bot: ChatbotConfig) {
    const url = this.router.serializeUrl(this.router.createUrlTree(["/" + this.langService.getLanguage() + '/bot/' + bot._id]));
    window.open(url, '_blank');
  }


  public isImporting = false;
  public importErrorDialogVisible = false;
  public importFailureMessage = '';
  public importFailureReport: BotImportFailureReport | null = null;

  public duplicateErrorDialogVisible = false;
  public duplicateFailureReport: BotImportFailureReport | null = null;

  public async onImportFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    input.value = '';
    this.isImporting = true;
    const formData = new FormData();
    formData.append('file', file);
    if (this.projectService.selectedProject) {
      formData.append('projectId', this.projectService.selectedProject._id);
    }
    try {
      const res = await this.adminService.importBotFromZip(formData);
      if (res.status === 'ok') {
        this.toastr.success('IMPORTED');
        // TODO: The 'emit' function requires a mandatory void argument
        this.reloadTrigger.emit();
      } else {
        this.showImportError(res);
      }
    } catch (error: unknown) {
      const payload = (error as { error?: BotImportApiResponse }).error;
      this.showImportError(payload ?? {
        status: 'error',
        message: $localize`:@@import-aborted-generic:Der Import wurde abgebrochen. Es wurden keine Änderungen gespeichert.`
      });
    } finally {
      this.isImporting = false;
    }
  }

  public hasImportReportContent(): boolean {
    if (!this.importFailureReport) {
      return false;
    }
    return this.importFailureReport.errors.length > 0 || this.importFailureReport.cleanupWarnings.length > 0;
  }

  private showImportError(response: BotImportApiResponse): void {
    const message = response.message ?? $localize`:@@import-aborted-generic:Der Import wurde abgebrochen. Es wurden keine Änderungen gespeichert.`;
    this.toastr.error('IMPORTED', message);
    this.importFailureMessage = message;
    this.importFailureReport = response.report ?? null;
    this.importErrorDialogVisible = this.hasImportReportContent();
  }

  private showDuplicateError(response: BotImportApiResponse): void {
    const hasReport = (response.report?.errors.length ?? 0) > 0 || (response.report?.cleanupWarnings.length ?? 0) > 0;
    if (hasReport) {
      this.duplicateFailureReport = response.report!;
      this.duplicateErrorDialogVisible = true;
    } else {
      const message = response.message ?? $localize`:@@duplicate-failed-generic:Das Duplizieren ist fehlgeschlagen. Versuche es erneut.`;
      this.toastr.baseToastr.error(message, $localize`:@@t-error-duplicated:Das Duplizieren ist fehlgeschlagen. Versuche es erneut.`, { timeOut: 8000 });
    }
  }

  public addBot() {
    this.showNewBotDialog = true;
  }

  public canChangeProject() {
    const project = this.projectService.projects.find(p => p._id == this.projectService.selectedProject?._id);
    return (project) ? this.projectService.canChange(project) : true;
  }

  public async trashBot(id: string) {
    await this.adminService.trashBot(id);
    const bot = this.bots().find(bot => bot._id === id);
    if (bot) {
      bot.thisIsTrash = true;
      this.selectedBots = this.selectedBots.filter(b => b._id !== id);
    }
    // TODO: The 'emit' function requires a mandatory void argument
    this.reloadTrigger.emit();
  }

  public confirmBulkTrash() {
    if (this.bulkBusy || !this.userCanEditBotConfig() || !this.selectedBots.length) return;
    const toTrash = [...this.selectedBots];
    const count = toTrash.length;
    this.confirmationService.confirm({
      header: $localize`:@@bulk-trash-header:${count}:count: Bot(s) in den Papierkorb verschieben?`,
      icon: 'ri-error-warning-line',
      message: $localize`:@@bulk-trash-message:Möchtest du ${count}:count: Bot(s) wirklich in den Papierkorb verschieben?`,
      rejectButtonProps: {
        label: $localize`:@@delete-reject:Abbrechen`,
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: $localize`:@@trashbin-accept:Papierkorb`,
        severity: 'danger'
      },
      accept: async () => {
        await this.runBulk(toTrash, id => this.adminService.trashBot(id));
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

  public async bulkArchive(): Promise<void> {
    await this.runBulk([...this.selectedBots], id => this.adminService.archiveBot(id));
  }

  private async runBulk(bots: ChatbotConfig[], action: (id: string) => Promise<unknown>): Promise<void> {
    if (this.bulkBusy || !this.userCanEditBotConfig() || !bots.length) return;
    this.bulkBusy = true;
    this.bulkFailedNames = '';
    try {
      const result = await runBotBatch(bots, action);
      this.selectedBots = result.failed;
      this.bulkFailedNames = result.failed.map(bot => bot.name).join(', ');
      await this.projectService.loadBots();
    } finally {
      this.bulkBusy = false;
    }
  }

  public async archiveBot(id: string) {
    await this.adminService.archiveBot(id);
    const bot = this.bots().find(bot => bot._id === id);
    if (bot) {
      bot.archived = true;
      this.selectedBots = this.selectedBots.filter(selected => selected._id !== id);
    }
    // TODO: The 'emit' function requires a mandatory void argument
    this.reloadTrigger.emit();
  }

  public async unarchiveBot(id: string) {
    await this.adminService.unarchiveBot(id);
    const bot = this.bots().find(bot => bot._id === id);
    if (bot) {
      bot.archived = false;
    }
    // TODO: The 'emit' function requires a mandatory void argument
    this.reloadTrigger.emit();
  }

  public async duplicateBot(id: string) {
    try {
      await this.adminService.copyBot(id);
      this.toastr.success('DUPLICATED');
      // TODO: The 'emit' function requires a mandatory void argument
      this.duplicateTrigger.emit();
    } catch (error: unknown) {
      const payload = (error as { error?: BotImportApiResponse }).error;
      this.showDuplicateError(payload ?? { status: 'error' });
    }
  }

  public userCanEditBotConfig(): boolean {
    if (!this.projectService.selectedProject) return false;
    return this.projectService.canChange(this.projectService.selectedProject);
  }

  public async botTransfer(bot: ChatbotConfig) {
    this.modalService.show(TransferBotModalComponent, { initialState: { bot, users: await this.adminService.getUsers() } });
  }

  public moveBotToProject(bot: ChatbotConfig) {
    const projectId = bot.projectId;
    this.modalService.show(MoveBotToProjectComponent, { initialState: { bot, projectId: projectId ?? "-" } });
  }

  public confirmationCheck(botId: string) {
    this.confirmationService.confirm({
      header: $localize`:@@confirm-delete-bot:Bot in den Papierkorb verschieben`,
      icon: 'ri-error-warning-line',
      message: $localize`:@@confirm-delete-message-bot:Möchtest du diesen Bot wirklich in den Papierkorb verschieben?`,
      rejectButtonProps: {
        label: $localize`:@@delete-reject:Abbrechen`,
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: $localize`:@@trashbin-accept:Papierkorb`,
        severity: 'danger'
      },
      accept: async () => {
        await this.trashBot(botId);
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }
}
